
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getAllStoredApiKeys, getAPIKey } from '@/utils/ai/apiKeyUtils';
import { testAllAPIKeys } from '@/utils/ai/testAPIKeys';
import { Eye, EyeOff, RefreshCw, TestTube } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const APIKeyDebug = () => {
  const [showDebug, setShowDebug] = useState(false);
  const [apiKeyInfo, setApiKeyInfo] = useState<Record<string, string | null>>({});
  const [isTestingKeys, setIsTestingKeys] = useState(false);

  // Check on mount for presence of API keys
  useEffect(() => {
    if (showDebug) {
      handleCheckApiKeys();
    }
  }, [showDebug]);

  const handleCheckApiKeys = () => {
    const keys = getAllStoredApiKeys();
    setApiKeyInfo(keys);
  };

  const handleTestApiKeys = async () => {
    setIsTestingKeys(true);
    try {
      const results = await testAllAPIKeys();
      
      if (results.openai.valid && results.perplexity.valid) {
        toast.success("All API keys are valid!");
      } else {
        const errors = [];
        if (!results.openai.valid) errors.push(`OpenAI: ${results.openai.error}`);
        if (!results.perplexity.valid) errors.push(`Perplexity: ${results.perplexity.error}`);
        toast.error(`API key validation failed: ${errors.join(', ')}`);
      }
    } catch (error) {
      toast.error("Failed to test API keys");
    } finally {
      setIsTestingKeys(false);
    }
  };

  // Get API key status directly
  const openaiKeyPresent = !!getAPIKey('openai');
  const perplexityKeyPresent = !!getAPIKey('perplexity');

  // If at least one key is missing, show alert status
  const missingKeys = !openaiKeyPresent || !perplexityKeyPresent;

  if (!showDebug) {
    return (
      <div className="flex justify-end mt-4">
        <Button 
          variant={missingKeys ? "destructive" : "outline"}
          size="sm"
          onClick={() => {
            setShowDebug(true);
            handleCheckApiKeys();
          }}
          className="flex items-center gap-2"
        >
          <Eye size={16} />
          {missingKeys ? "API Keys Missing" : "API Key Status"}
        </Button>
      </div>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">API Key Debug</CardTitle>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleTestApiKeys}
            disabled={isTestingKeys}
            className="flex items-center gap-1"
          >
            <TestTube size={14} />
            {isTestingKeys ? "Testing..." : "Test Keys"}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCheckApiKeys}
            className="flex items-center gap-1"
          >
            <RefreshCw size={14} />
            Refresh
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowDebug(false)}
            className="flex items-center gap-1"
          >
            <EyeOff size={14} />
            Hide
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between items-center mb-3">
            <p className="text-xs text-muted-foreground">
              This panel shows if API keys are present in localStorage.
            </p>
            
            <div className="flex gap-2">
              <Badge variant={openaiKeyPresent ? "success" : "destructive"} className="text-xs">
                OpenAI: {openaiKeyPresent ? "Present" : "Missing"}
              </Badge>
              
              <Badge variant={perplexityKeyPresent ? "success" : "destructive"} className="text-xs">
                Perplexity: {perplexityKeyPresent ? "Present" : "Missing"}
              </Badge>
            </div>
          </div>
          
          <div className="space-y-1 text-xs">
            {Object.entries(apiKeyInfo).map(([key, value]) => (
              <div key={key} className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="font-mono">{key}</span>
                <span className={`${value ? 'text-green-500' : 'text-red-500'}`}>
                  {value ? 'Present' : 'Missing'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default APIKeyDebug;
